import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { searchRows, sortRows } from '@/shared/utils/tableControls';

/**
 * Search, status filter and sort for a table. Rows are sorted, then
 * filtered, so the shown rows and `allRows` share one order; with no sort
 * chosen the incoming order is kept. `statusOf(row)`
 * enables the status filter. Returns the rows to show, and the controls'
 * state for the shared bar, header cells and export menu. `departmentOf(row)`
 * enables a second filter beside the status one, for the row's department;
 * `departmentOptions` (the select's options, or null when it is hidden) lets
 * the hook drop a chosen department the options no longer offer, such as one
 * carried over a term change or restored from the URL, so the applied filter
 * is always the one the select shows. `persist` (from `useTableParams`) restores the state on mount and saves each
 * change.
 */
export function useTableControls({
    rows,
    columns,
    statusOf = null,
    departmentOf = null,
    departmentOptions = undefined,
    persist = null,
}) {
    const [search, setSearch] = useState(persist?.initial.search ?? '');
    const [status, setStatus] = useState(persist?.initial.status ?? 'all');
    const [department, setDepartment] = useState(
        persist?.initial.department ?? 'all'
    );
    const [sort, setSort] = useState(
        persist?.initial.sort ?? { key: null, direction: 'asc' }
    );

    // Waits for rows so a value restored from the URL survives the load
    const departmentOffered =
        departmentOptions === undefined ||
        rows.length === 0 ||
        department === 'all' ||
        Boolean(departmentOptions?.some((o) => o.value === department));
    useEffect(() => {
        if (!departmentOffered) setDepartment('all');
    }, [departmentOffered]);

    const saved = useRef({ search, status, department, sort });
    useEffect(() => {
        if (!persist) return;
        const last = saved.current;
        if (
            last.search === search &&
            last.status === status &&
            last.department === department &&
            last.sort === sort
        ) {
            return;
        }
        saved.current = { search, status, department, sort };
        persist.save({ search, status, department, sort });
    }, [persist, search, status, department, sort]);

    const ordered = useMemo(() => {
        const column = columns.find((c) => c.key === sort.key);
        return sortRows(rows, column, sort.direction);
    }, [rows, columns, sort]);

    const shownRows = useMemo(() => {
        let result = searchRows(ordered, columns, search);
        if (statusOf && status !== 'all') {
            result = result.filter((row) => statusOf(row) === status);
        }
        if (departmentOf && department !== 'all') {
            result = result.filter((row) => departmentOf(row) === department);
        }
        return result;
    }, [ordered, columns, search, status, statusOf, department, departmentOf]);

    const toggleSort = useCallback(
        (key) =>
            setSort((prev) =>
                prev.key === key
                    ? {
                          key,
                          direction: prev.direction === 'asc' ? 'desc' : 'asc',
                      }
                    : { key, direction: 'asc' }
            ),
        []
    );

    const ariaSort = (key) => {
        if (sort.key !== key) return 'none';
        return sort.direction === 'asc' ? 'ascending' : 'descending';
    };

    const filtersActive =
        search.trim() !== '' ||
        Boolean(statusOf && status !== 'all') ||
        Boolean(departmentOf && department !== 'all');
    // The Clear filters button unmounts as it is used; focus goes to the
    // search field so a keyboard user is not dropped at the top of the page
    const searchRef = useRef(null);
    const clearFilters = useCallback(() => {
        setSearch('');
        setStatus('all');
        setDepartment('all');
        searchRef.current?.focus();
    }, []);

    return {
        rows: shownRows,
        allRows: ordered,
        total: rows.length,
        filtersActive,
        clearFilters,
        searchRef,
        columns,
        search,
        setSearch,
        status,
        setStatus,
        department,
        setDepartment,
        sort,
        setSort,
        toggleSort,
        ariaSort,
    };
}

export default useTableControls;
